import React, { useEffect, useMemo, useState } from "react";
import { Checkbox, Divider, Dropdown } from "antd";
import { ReactComponent as SixDotsIcon } from "../../img/six-dots.svg";
import { ISearchFilterField } from "../../lib/types";
import "./AccountBulkFiltersToggler.less";

interface ComponentProps {
  filterName: string;
  allFields: ISearchFilterField[];
  setFields: React.Dispatch<React.SetStateAction<ISearchFilterField[]>>;
  disabledFieldsName?: string[];
}

export const AccountBulkFiltersToggler = (props: ComponentProps) => {
  const { allFields, filterName, setFields, disabledFieldsName } = props;
  const storageData = useMemo(() => localStorage.getItem(`${filterName}_filter`), [filterName]);

  const [savedFieldNames, setSavedFieldNames] = useState<string[]>([]);

  // Установка исходных значений из localStorage, если есть
  useEffect(() => {
    if (storageData) {
      setSavedFieldNames(JSON.parse(storageData));
    } else {
      setSavedFieldNames(allFields.filter(({ visible }) => visible).map(({ label }) => label));
    }
  }, [storageData]);

  const [isDropdownVisible, setIsDropdownVisible] = useState(false);

  // Изменение порядка следования полей, по изменении видимости
  useEffect(() => {
    if (savedFieldNames.length > 0) {
      const updatedFields = allFields.map((field) => ({
        ...field,
        visible: savedFieldNames.includes(field.label),
      }));
      updatedFields.sort((a, b) => {
        const aPos = savedFieldNames.indexOf(a.label);
        const bPos = savedFieldNames.indexOf(b.label);
        return aPos > bPos ? 1 : -1;
      });
      setFields(updatedFields);
      localStorage.setItem(`${filterName}_filter`, JSON.stringify(savedFieldNames));
    }
  }, [savedFieldNames, allFields]);

  const onChange = (checkedValues) => {
    const isItemAdded = savedFieldNames.length < checkedValues.length;
    if (isItemAdded) {
      const [lastCheckedValue] = checkedValues.filter((value) => !savedFieldNames.includes(value));
      setSavedFieldNames([...savedFieldNames, lastCheckedValue]);
    } else {
      setSavedFieldNames(checkedValues);
    }
  };

  const options = useMemo(() => {
    const visibleFieldsOptions = savedFieldNames.map((label) => {
      const isDisabled = disabledFieldsName?.includes(label);
      return {
        label,
        value: label,
        disabled: savedFieldNames.length === 1 || isDisabled,
      };
    });
    const hiddenFieldsOptions = allFields
      .filter(({ label }) => !savedFieldNames.includes(label))
      .map(({ label }) => ({ label, value: label }))
      .sort((a, b) => (a.label > b.label ? 1 : -1));
    return [...visibleFieldsOptions, ...hiddenFieldsOptions];
  }, [savedFieldNames, allFields]);

  const onChangeAll = (checked: boolean) => {
    if (checked) {
      const hiddenFieldNames = options
        .filter(({ label }) => !savedFieldNames.includes(label))
        .map(({ label }) => label);
      const updatedFieldNames = [...savedFieldNames, ...hiddenFieldNames];
      setSavedFieldNames(updatedFieldNames);
      localStorage.setItem(`${filterName}_filter`, JSON.stringify(updatedFieldNames));
    } else {
      const minSave =
        disabledFieldsName && disabledFieldsName.length > 0
          ? disabledFieldsName
          : [allFields[0].label];
      setSavedFieldNames(minSave);
      localStorage.setItem(`${filterName}_filter`, JSON.stringify(minSave));
    }
  };

  const dropdownMenu = (
    <div className="bulkFilters-toggler__dropdownMenu">
      <Checkbox
        onChange={(e) => onChangeAll(e.target.checked)}
        indeterminate={savedFieldNames.length > 0 && savedFieldNames.length !== allFields.length}
        checked={savedFieldNames.length === allFields.length}
      >
        Выбрать / снять все
      </Checkbox>
      <Divider style={{ margin: "5px" }} />
      <Checkbox.Group
        className="checkbox-group"
        options={options}
        value={savedFieldNames}
        onChange={onChange}
      />
    </div>
  );

  return (
    <div className="bulkFilters-toggler">
      <Dropdown
        trigger={["click"]}
        visible={isDropdownVisible}
        onVisibleChange={(visible: boolean) => setIsDropdownVisible(visible)}
        overlay={dropdownMenu}
        className="toggle-columns"
        overlayClassName="toggle-columns_dropdown"
      >
        <SixDotsIcon className="toggle-columns__dots-icon" />
      </Dropdown>
    </div>
  );
};
